import { useEffect, useMemo, useState } from 'react';
import { loadProductCatalogFilters } from '../pages/AdminPanel/Products/service/product-catalog-filters-service';
import {
  buildProductFilterKey,
  isCardInProductFilter,
  isCategoryInProductFilter,
  resolveCatalogSourcesFromAccess,
} from '../utils/productCatalogFilterKeys';

export function useProductCatalogFiltersForCard({
  enabled = true,
  categoryId,
  cardId,
  selectedProducts = [],
  selectedPlans = [],
  selectedCourses = [],
}) {
  const [groups, setGroups] = useState([]);
  const [initialSelectedKeys, setInitialSelectedKeys] = useState([]);
  const [loading, setLoading] = useState(false);

  const accessKey = useMemo(
    () =>
      JSON.stringify({
        products: selectedProducts.map((item) => item.docId || item.id).filter(Boolean).sort(),
        plans: selectedPlans.map((item) => item.id).filter(Boolean).sort(),
        courses: selectedCourses.map((item) => item.id).filter(Boolean).sort(),
      }),
    [selectedProducts, selectedPlans, selectedCourses]
  );

  useEffect(() => {
    if (!enabled || !categoryId || !cardId) {
      setGroups([]);
      setInitialSelectedKeys([]);
      setLoading(false);
      return;
    }

    const sources = resolveCatalogSourcesFromAccess({
      selectedProducts,
      selectedPlans,
      selectedCourses,
    });

    if (sources.length === 0) {
      setGroups([]);
      setInitialSelectedKeys([]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const loadedGroups = await Promise.all(
          sources.map(async (source) => {
            const filters = await loadProductCatalogFilters(source.collectionName, source.docId);
            const activeFilters = filters.filter((filter) => !filter.archived);

            const mappedFilters = activeFilters.map((filter) => {
              const inherited = isCategoryInProductFilter(filter, categoryId);
              const linked = inherited || isCardInProductFilter(filter, categoryId, cardId);

              return {
                id: filter.id,
                name: filter.name,
                slug: filter.slug,
                filterKey: buildProductFilterKey(source.collectionName, source.docId, filter.id),
                inherited,
                linked,
              };
            });

            return {
              ...source,
              filters: mappedFilters,
            };
          })
        );

        if (cancelled) return;

        const withFilters = loadedGroups.filter((group) => group.filters.length > 0);
        setGroups(withFilters);

        const linkedKeys = withFilters.flatMap((group) =>
          group.filters
            .filter((filter) => filter.linked && !filter.inherited)
            .map((filter) => filter.filterKey)
        );
        setInitialSelectedKeys(linkedKeys);
      } catch (err) {
        console.error('Erro ao carregar filtros por produto:', err);
        if (!cancelled) {
          setGroups([]);
          setInitialSelectedKeys([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, categoryId, cardId, accessKey, selectedProducts, selectedPlans, selectedCourses]);

  return { groups, initialSelectedKeys, loading };
}
